// components/PrivateRoute.jsx
// UPDATED: Works with stable AuthContext without flickering

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import UniversalPHICheck from "./UniversalPHICheck";

const PrivateRoute = ({ children, requiredRole = null }) => {
  const { isAuthenticated, loading, user, isInitialized } = useAuth();
  const location = useLocation();

  // Show loading state while initializing or checking authentication
  // Don't show loading if we're not initialized yet
  if (!isInitialized || loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>🔐 Verifying secure session...</p>
          <small className="text-muted">
            HIPAA-compliant authentication in progress...
          </small>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  // Only redirect if we're fully initialized to prevent premature redirects
  if (!isAuthenticated || !user) {
    console.log("🚪 Redirecting to login - not authenticated");
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access if required
  if (requiredRole) {
    const userRole = user.role?.toLowerCase();
    const hasRequiredRole = Array.isArray(requiredRole)
      ? requiredRole.some((role) => role.toLowerCase() === userRole)
      : userRole === requiredRole.toLowerCase();

    if (!hasRequiredRole) {
      console.log("🚫 Access denied - insufficient role:", {
        userRole,
        requiredRole,
      });

      return (
        <div className="container mt-5">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">🚫 Access Denied</h4>
            <p>You don't have permission to access this page.</p>
            <hr />
            <p className="mb-0">
              <strong>Your role:</strong> {user.role}
              <br />
              <strong>Required role:</strong>{" "}
              {Array.isArray(requiredRole)
                ? requiredRole.join(", ")
                : requiredRole}
            </p>
            <div className="mt-3">
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => window.history.back()}
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // ✅ User is authenticated and has required role
  // Wrap with PHI check for HIPAA compliance
  return <UniversalPHICheck>{children}</UniversalPHICheck>;
};

export default PrivateRoute;
